import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum, type RumEvent} from '@datadog/browser-rum';
import {reactPlugin} from '@datadog/browser-rum-react';

import {
  datadogApplicationId,
  datadogClientToken,
  environment,
  releaseName,
} from '~/shared/config';
import renderingForRobot from '~/shared/services/renderingForRobot';
import {datadogSite, datadogService} from '~/shared/logging/datadogConsts';

/**
 * Error messages that are never actionable. These only ever apply to *uncaught* errors
 * (`source: 'source'`), since every other remote report goes through an explicit
 * `{sendReport: true}` call. Keep the list evidence-based: add an entry only after
 * confirming the error is handled, transient, third-party or environmental, and say WHY.
 */
const ignoredErrorMessages: (string | RegExp)[] = [
  // Web Storage blocked (private mode / in-app webviews) — localStorage is null.
  // Defended at source via `safeLocalStorage`/`safeSessionStorage`; this is a thin net
  // for un-migrated and third-party callers.
  /Cannot read properties of null \(reading 'getItem'\)/i,
  /null is not an object \(evaluating '.*\.getItem'\)/i, // Safari phrasing of the same
  // Handled errors thrown as plain strings/objects surface as these synthetic wrappers.
  // The user already saw a UI error state.
  'Non-Error promise rejection captured',
  'Object captured as promise rejection',
  // User-facing "something went wrong, try again later" — already shown to the user.
  'משהו השתבש, נסו שוב מאוחר יותר',
  // Transient connectivity — not actionable per-event; real outages are caught server-side.
  'Network Error',
  /timeout of \d+ms exceeded/i,
  /Failed to fetch.*cloudfront/i, // CDN asset fetch blips
  // Stale deploy: a cached index.html references a chunk that no longer exists.
  // Better long-term fix is reloading the page on ChunkLoadError instead of ignoring it.
  'ChunkLoadError',
  /Loading (CSS )?chunk .* failed/i,
  // Third-party SDK noise.
  /Document is ready and element paypal-button.* does not exist/i, // PayPal SDK
  // In-app webview bridges (the native app wrapping the site) — irrelevant to the web app.
  'window.webkit.messageHandlers', // iOS
  'Java object is gone', // Android
  // Browser extensions / injected scripts, which we cannot fix.
  'Script error.',
];

/**
 * Third-party origins whose failures are not ours. Matched against the error stack,
 * which is where the originating script URL appears.
 */
const deniedErrorUrls: RegExp[] = [
  /chrome-extension:/i,
  /^chrome:\/\//i,
  /extensions\//i,
  /moz-extension:/i,
  /safari-extension:/i,
  /graph\.facebook\.com/i,
  /connect\.facebook\.net/i,
  /maps\.googleapis\.com/i,
  /www\.google-analytics\.com/i,
  /stats\.g\.doubleclick\.net/,
  /\.appsflyer\.com/i,
  /\.hotjar\.(io|com)/i,
  /\.intercom\.com/i,
  /script\.crazyegg\.com/,
  /ekr\.zdassets\.com/,
  /dc\.services\.visualstudio\.com/,
  /bam\..*\.nr-data\.net/,
  /utag/,
];

/**
 * Datadog is only active when `init` succeeded. Every exported helper below is a no-op
 * otherwise, so the app runs exactly as before when `DD_CLIENT_TOKEN` is not provided.
 */
let isEnabled = false;

/**
 * Identity of errors already sent through `addError`. RUM also auto-collects uncaught
 * errors on its own, so an error that was reported with `sendReport: true` and then
 * rethrown (or re-rejected) would otherwise reach Datadog twice.
 */
const MAX_REPORTED_KEYS = 100;
const reportedErrorKeys = new Set<string>();

const getErrorKey = ({message, stack}: {message?: string; stack?: string}) => `${message || ''}\n${stack || ''}`;

function markReported(error: Error) {
  if (reportedErrorKeys.size >= MAX_REPORTED_KEYS) {
    reportedErrorKeys.delete(reportedErrorKeys.values().next().value as string);
  }

  reportedErrorKeys.add(getErrorKey(error));
}

function isIgnoredError({message, stack}: {message: string; stack?: string}) {
  const text = message || '';

  if (ignoredErrorMessages.some(pattern => (typeof pattern === 'string' ? text.includes(pattern) : pattern.test(text)))) {
    return true;
  }

  return !!stack && deniedErrorUrls.some(pattern => pattern.test(stack));
}

/**
 * Datadog RUM collects `console.error` calls and failed network requests as errors by
 * default. Our logger writes to the console on *every* `logger.error`, and the http layer
 * logs every failed request — so without this allow-list every handled error would land
 * in Error Tracking and the `sendReport` opt-in would be meaningless.
 *
 * Only two sources are allowed through:
 * - `custom` — came from `addError()`, which is only reached via `{sendReport: true}`.
 * - `source` — a genuinely uncaught error or unhandled rejection (nobody handled it,
 *   which is exactly the `sendReport` criteria), minus the ignore lists above.
 */
export const beforeSend = (event: RumEvent) => {
  if (event.type !== 'error') {
    return true;
  }

  switch (event.error.source) {
    case 'custom':
      return true;
    case 'source':
      // Already sent via `addError` — don't send the same error a second time.
      return !reportedErrorKeys.has(getErrorKey(event.error)) && !isIgnoredError(event.error);
    // 'console' | 'network' | 'report' | 'logger' | 'agent'
    default:
      return false;
  }
};

export function init() {
  // Bot traffic (prerender) would burn RUM sessions without producing anything useful.
  if (renderingForRobot) {
    return;
  }

  // No token (e.g. dev / staging builds) -> Datadog stays completely off.
  if (!datadogClientToken) {
    return;
  }

  datadogRum.init({
    applicationId: datadogApplicationId,
    clientToken: datadogClientToken,
    site: datadogSite,
    // `service` and `version` must match the source map upload in webpack.config.js,
    // otherwise Error Tracking cannot de-minify stack traces.
    service: datadogService,
    version: releaseName,
    env: environment,
    plugins: [reactPlugin()],
    // 100% of sessions: an error belonging to an unsampled session is dropped, and we
    // would rather pay for sessions than lose reports. Lower this if volume becomes an issue.
    sessionSampleRate: 50,
    // Session Replay is off. Turn it on by raising this rate — it is billed separately.
    sessionReplaySampleRate: 10,
    // Keeps user actions (clicks, taps) on the session timeline, which is the context
    // attached to an error. Cheap, and the closest thing to the breadcrumbs we dropped.
    trackUserInteractions: true,
    // Resource and long-task events are pure volume for our purposes.
    trackResources: false,
    trackLongTasks: false,
    beforeSend,
  });

  // Browser logs power the Log Explorer copy of every `sendReport: true` error. Console
  // errors, network errors and reports are NOT forwarded automatically — only explicit
  // `datadogLogs.logger` calls (see `reportError`) are sent.
  datadogLogs.init({
    clientToken: datadogClientToken,
    site: datadogSite,
    service: datadogService,
    version: releaseName,
    env: environment,
    forwardErrorsToLogs: false,
    forwardConsoleLogs: [],
    sessionSampleRate: 100,
  });

  isEnabled = true;
}

type ReportErrorParams = {
  loggerName: string;
  error: Error;
  args?: any[];
};

/**
 * Replaces an `Error` with a plain object so it survives JSON serialization into the
 * Datadog event context (an `Error` would otherwise serialize to `{}`).
 */
function serializeExtra(value: unknown): unknown {
  if (value instanceof Error) {
    return {name: value.name, message: value.message, stack: value.stack};
  }

  return value;
}

/**
 * Sends an error to Datadog Error Tracking. Only called for `{sendReport: true}` — see
 * `trackError` in ./logging.
 */
export function reportError({loggerName, error, args}: ReportErrorParams) {
  if (!isEnabled) {
    return;
  }

  const fingerprint = args?.find(item => item?.fingerprint)?.fingerprint;

  if (fingerprint) {
    // Datadog reads a custom Error Tracking grouping key off the error object itself.
    (error as Error & {dd_fingerprint?: string}).dd_fingerprint = String(fingerprint);
  }

  // Mark before sending so the auto-collected copy of this same error is dropped.
  markReported(error);

  const extras = args?.map(serializeExtra);

  datadogRum.addError(error, {
    logger: loggerName,
    extras,
  });

  // Same report, stored in the Log Explorer (correlated to the RUM session automatically).
  datadogLogs.logger.error(error.message, {logger: loggerName, extras}, error);
}

/** Attaches the signed-in user to every RUM event. Pass `null` on sign-out. */
export function setUser(user: {id: string; email?: string; companyId?: string | number; isCompanyUser?: boolean} | null) {
  if (!isEnabled) {
    return;
  }

  if (!user) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser(user);
}

/** Attaches an arbitrary key to every RUM event (view, action, error) for free. */
export function setGlobalContext(key: string, value: unknown) {
  if (!isEnabled) {
    return;
  }

  datadogRum.setGlobalContextProperty(key, value);
}

/**
 * Renames the current RUM view after our route instead of the raw URL.
 *
 * Deliberately `setViewName` and not `startView`: RUM already creates a view on every
 * history change, so `startView` would end that view and open a second one — two view
 * events per navigation. `setViewName` relabels the existing view and emits nothing.
 */
export function setViewName(name: string) {
  if (!isEnabled) {
    return;
  }

  datadogRum.setViewName(name);
}
